import React from 'react';
import {
	Typography,
	InputAdornment,
	FormControl,
	InputLabel,
	MenuItem,
	Box,
	TableHead,
	TableBody,
	Pagination,
	PaginationItem,
} from '@mui/material';
import {
	SearchRounded,
	CloseRounded,
	ChevronLeftRounded,
	ChevronRightRounded,
	KeyboardDoubleArrowLeftRounded,
	KeyboardDoubleArrowRightRounded,
} from '@mui/icons-material';
import { AUDIT_ENTITY_LABELS, EAuditEntity, IAuditLog } from '../../api/audit';
import { EUserRole } from '../../types/user';
import {
	PageContainer,
	FiltersContainer,
	SearchField,
	FilterSelect,
	AppliedFiltersContainer,
	FilterChip,
	ClearFiltersButton,
	ResultsHeader,
	StyledTableContainer,
	StyledTable,
	StyledTableHeaderCell,
	StyledTableCell,
	StyledTableRow,
	UserCell,
	UserAvatar,
	EmptyStateContainer,
	Pager,
} from './styles';

const getRolLabel = (rol: string): string => {
	const rolMap: Record<string, string> = {
		ALUMNO: 'Alumno',
		DOCENTE: 'Docente',
		AUDITOR: 'Auditor',
		ADMINISTRADOR: 'Administrador',
	};
	return rolMap[rol] ?? rol;
};

interface AuditoriaPageViewProps {
	logs: IAuditLog[];
	total: number;
	page: number;
	pageSize: number;
	onPageChange: (page: number) => void;
	loadError: string | null;
	searchQuery: string;
	onSearchChange: (query: string) => void;
	selectedEntidad: EAuditEntity | 'all';
	onEntidadChange: (entidad: EAuditEntity | 'all') => void;
	selectedRol: EUserRole | 'all';
	onRolChange: (rol: EUserRole | 'all') => void;
	dateRange: string;
	onDateRangeChange: (range: string) => void;
	onResetFilters: () => void;
}

const getDateRangeLabel = (range: string): string => {
	const rangeMap: Record<string, string> = {
		'últimos-7-días': 'Últimos 7 días',
		'últimos-30-días': 'Últimos 30 días',
		'últimos-90-días': 'Últimos 90 días',
	};
	return rangeMap[range] ?? range;
};

const AuditoriaPageView: React.FC<AuditoriaPageViewProps> = ({
	logs,
	total,
	page,
	pageSize,
	onPageChange,
	loadError,
	searchQuery,
	onSearchChange,
	selectedEntidad,
	onEntidadChange,
	selectedRol,
	onRolChange,
	dateRange,
	onDateRangeChange,
	onResetFilters,
}) => {
	const getInitials = (name: string) => {
		const parts = name.split(' ');
		if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
		return name.substring(0, 2).toUpperCase();
	};

	const formatDate = (date: Date) =>
		date.toLocaleDateString('es-MX', {
			day: '2-digit',
			month: 'short',
			year: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
		});

	const hasActiveFilters =
		searchQuery !== '' ||
		selectedEntidad !== 'all' ||
		selectedRol !== 'all' ||
		dateRange !== 'últimos-30-días';

	const appliedFilters: { label: string; onDelete: () => void }[] = [];
	if (selectedEntidad !== 'all') {
		appliedFilters.push({
			label: AUDIT_ENTITY_LABELS[selectedEntidad],
			onDelete: () => onEntidadChange('all'),
		});
	}
	if (selectedRol !== 'all') {
		appliedFilters.push({
			label: getRolLabel(selectedRol),
			onDelete: () => onRolChange('all'),
		});
	}
	if (dateRange !== 'últimos-30-días') {
		appliedFilters.push({
			label: getDateRangeLabel(dateRange),
			onDelete: () => onDateRangeChange('últimos-30-días'),
		});
	}

	return (
		<PageContainer>
			{loadError && (
				<Typography color='error' variant='body2'>
					{loadError}
				</Typography>
			)}
			{/* Filters */}
			<FiltersContainer>
				<SearchField
					fullWidth
					placeholder='Buscar por acción o usuario...'
					value={searchQuery}
					onChange={(e) => onSearchChange(e.target.value)}
					InputProps={{
						startAdornment: (
							<InputAdornment position='start'>
								<SearchRounded />
							</InputAdornment>
						),
					}}
				/>

				<FormControl fullWidth>
					<InputLabel id='date-range-label'>Rango de fechas</InputLabel>
					<FilterSelect
						labelId='date-range-label'
						value={dateRange}
						label='Rango de fechas'
						onChange={(e) => onDateRangeChange(e.target.value as string)}
					>
						<MenuItem value='últimos-7-días'>Últimos 7 días</MenuItem>
						<MenuItem value='últimos-30-días'>Últimos 30 días</MenuItem>
						<MenuItem value='últimos-90-días'>Últimos 90 días</MenuItem>
					</FilterSelect>
				</FormControl>

				<FormControl fullWidth>
					<InputLabel id='entidad-label'>Entidad</InputLabel>
					<FilterSelect
						labelId='entidad-label'
						value={selectedEntidad}
						label='Entidad'
						onChange={(e) => onEntidadChange(e.target.value as EAuditEntity | 'all')}
					>
						<MenuItem value='all'>Todas las entidades</MenuItem>
						{(Object.keys(AUDIT_ENTITY_LABELS) as EAuditEntity[]).map((entity) => (
							<MenuItem key={entity} value={entity}>
								{AUDIT_ENTITY_LABELS[entity]}
							</MenuItem>
						))}
					</FilterSelect>
				</FormControl>

				<FormControl fullWidth>
					<InputLabel id='rol-label'>Rol</InputLabel>
					<FilterSelect
						labelId='rol-label'
						value={selectedRol}
						label='Rol'
						onChange={(e) => onRolChange(e.target.value as EUserRole | 'all')}
					>
						<MenuItem value='all'>Todos los roles</MenuItem>
						<MenuItem value={EUserRole.ALUMNO}>Alumno</MenuItem>
						<MenuItem value={EUserRole.DOCENTE}>Docente</MenuItem>
						<MenuItem value={EUserRole.AUDITOR}>Auditor</MenuItem>
						<MenuItem value={EUserRole.ADMINISTRADOR}>Administrador</MenuItem>
					</FilterSelect>
				</FormControl>
			</FiltersContainer>

			{/* Applied Filters */}
			{hasActiveFilters && (
				<AppliedFiltersContainer>
					{appliedFilters.map((filter, index) => (
						<FilterChip
							key={index}
							label={filter.label}
							onDelete={filter.onDelete}
							deleteIcon={<CloseRounded />}
						/>
					))}
					<ClearFiltersButton onClick={onResetFilters}>
						<CloseRounded fontSize='small' />
						<span>Limpiar filtros</span>
					</ClearFiltersButton>
				</AppliedFiltersContainer>
			)}

			{/* Results Header */}
			<ResultsHeader>
				<Typography variant='h6' color='text.primary'>
					{total.toLocaleString('es-MX')} registro{total !== 1 ? 's' : ''} encontrado
					{total !== 1 ? 's' : ''}
				</Typography>
			</ResultsHeader>

			{/* Table */}
			{logs.length > 0 ? (
				<StyledTableContainer>
					<StyledTable>
						<TableHead>
							<StyledTableRow>
								<StyledTableHeaderCell>Acción</StyledTableHeaderCell>
								<StyledTableHeaderCell>Usuario</StyledTableHeaderCell>
								<StyledTableHeaderCell>Rol</StyledTableHeaderCell>
								<StyledTableHeaderCell>Fecha</StyledTableHeaderCell>
								<StyledTableHeaderCell>Entidad</StyledTableHeaderCell>
							</StyledTableRow>
						</TableHead>
						<TableBody>
							{logs.map((log) => {
								const userName = log.actorName || 'Usuario desconocido';

								return (
									<StyledTableRow key={log.id}>
										{/* Acción */}
										<StyledTableCell>
											<Typography variant='body2'>{log.accion}</Typography>
											{log.detail && (
												<Typography variant='caption' color='text.secondary'>
													{log.detail}
												</Typography>
											)}
										</StyledTableCell>

										{/* Usuario */}
										<StyledTableCell>
											<UserCell>
												<UserAvatar src={log.actorAvatarUrl ?? undefined}>
													{getInitials(userName)}
												</UserAvatar>
												<Box>
													<Typography variant='body2' fontWeight={500} noWrap>
														{userName}
													</Typography>
													<Typography variant='caption' color='text.secondary'>
														{getRolLabel(log.rol)}
													</Typography>
												</Box>
											</UserCell>
										</StyledTableCell>

										{/* Rol */}
										<StyledTableCell>
											<Typography variant='body2' color='text.secondary'>
												{getRolLabel(log.rol)}
											</Typography>
										</StyledTableCell>

										{/* Fecha */}
										<StyledTableCell>
											<Typography variant='body2' color='text.secondary' noWrap>
												{formatDate(log.fecha)}
											</Typography>
										</StyledTableCell>

										{/* Entidad */}
										<StyledTableCell>
											<Typography variant='body2' color='text.secondary'>
												{AUDIT_ENTITY_LABELS[log.entidad]}
											</Typography>
										</StyledTableCell>
									</StyledTableRow>
								);
							})}
						</TableBody>
					</StyledTable>
				</StyledTableContainer>
			) : (
				<EmptyStateContainer>
					<Typography variant='h6' color='text.secondary' gutterBottom>
						No se encontraron registros
					</Typography>
					<Typography variant='body2' color='text.secondary'>
						Intenta ajustar los filtros de búsqueda para ver más resultados.
					</Typography>
				</EmptyStateContainer>
			)}

			{total > pageSize && (
				<Pager>
					<Pagination
						count={Math.ceil(total / pageSize)}
						page={page}
						onChange={(_, value) => onPageChange(value)}
						color='primary'
						shape='rounded'
						showFirstButton
						showLastButton
						renderItem={(item) => (
							<PaginationItem
								{...item}
								slots={{
									previous: ChevronLeftRounded,
									next: ChevronRightRounded,
									first: KeyboardDoubleArrowLeftRounded,
									last: KeyboardDoubleArrowRightRounded,
								}}
							/>
						)}
					/>
				</Pager>
			)}
		</PageContainer>
	);
};

export default AuditoriaPageView;
